/**
 * **タイミング調節ページ**（Issue #643。`/duel-timing`）。実写に演出を重ね、**演出の時刻を実写に合わせる**。
 * 仕様（イベントと応答の対応・プロジェクトファイル）は `docs/duel-timing.md`。
 *
 * - タイムライン（`duelTimeline/DuelTimeline.js`）: 演出の帯をドラッグ → その帯のイベントの時刻を動かす
 * - **設定の変化**: 舞台の設定（フィールド・カメラ・HUD・層の濃さ）をタイムラインに置き、再生位置が通ったらその値に変わる
 * - 保存: replay の timing にプロジェクトファイル（v4）。**変えたときだけ**。入力で作った応答（ops）は**書かない**
 * - `?duel=<id>&game=<uuid>`（`&replay=<id>`）で試合を開く（書き出しは Godot。ここで撮る `?render=1` は Issue #981 で消した）
 *
 * 演出とテーマの見た目の確認は別のページ（`/duel-editor`。`pages/DuelEditorDemo.js`）。舞台の組み立ては共通（`useDuelStage`）。
 * **テーマを詰めた端末の値（localStorage）はここでは使わない**（文書のテーマだけで決まる絵にするため）。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePageTitle } from '../appInfo';
import { api } from '../api/api';
import { useDuelStage } from './useDuelStage';
import Canvas, { CANVAS } from '../duelEditor/canvas/Canvas';
import { DEFAULT_THEME, THEMES, themeByName } from '../duelEditor/themes';
import { emptyDocument } from '../duelEditor/timeline/document';
import DuelTimeline from '../duelTimeline/DuelTimeline';
import FpsMeter from '../duelTimeline/FpsMeter';
import PreviewSettings from '../duelTimeline/PreviewSettings';
import SettingsEditor from '../duelTimeline/SettingsEditor';
import StageVideo from '../duelTimeline/StageVideo';
import { useSpaceKey } from '../duelTimeline/useSpaceKey';
import { formatClock } from '../duelTimeline/timelineGeometry';
import { createOcgStorage } from '../editorPorts/ocgStorage';
import {
  PREVIEW_QUALITIES, STAGE_HEIGHT, loadPreviewPrefs, qualityById, savePreviewPrefs,
} from '../editorPorts/previewQuality';
import {
  cleanTimingSettings, describeChange, nextChangeId, settingsAt,
} from '../editorPorts/timingSettings';

const box = { border: '1px solid #ccc', borderRadius: 8, padding: 10, marginBottom: 10 };
const h4 = { margin: '0 0 8px', fontSize: 14 };
const panel = {
  flex: '0 1 380px', minWidth: 300, position: 'sticky', top: 'calc(var(--nav-h) + 8px)',
  maxHeight: 'calc(100dvh - var(--nav-h) - 32px)', overflowY: 'auto', overscrollBehavior: 'contain', paddingRight: 6,
};

export default function DuelTiming() {
  usePageTitle('タイミング調節');

  const [params] = useState(() => new URLSearchParams(window.location.search));
  const [duels, setDuels] = useState([]);
  const [duelId, setDuelId] = useState(() => params.get('duel') ?? '');
  const [gameUuid, setGameUuid] = useState(() => params.get('game') ?? '');
  const [videoError, setVideoError] = useState(null);
  const [listError, setListError] = useState(null);

  // プレビューの大きさ・軽さ・fps（端末ごと）
  const [preview, setPreview] = useState(() => loadPreviewPrefs());
  const changePreview = useCallback((next) => { setPreview(next); savePreviewPrefs(next); }, []);
  const stageExtra = useMemo(() => qualityById(preview.quality).stage, [preview.quality]);

  /*
   * **設定は文書の中**（`meta.settings` = `{ base, changes }`、`meta.theme`）。editor は useDuelStage が作るので、
   * 最初の描画ではまだ無い —— ref で受けて、その時刻の設定を**値で** memo する（毎フレーム新しい object を渡すと
   * 盤の紙が毎フレーム描き直しになる）
   */
  const editorRef = useRef(null);
  const doc = editorRef.current?.doc ?? null;
  const theme = themeByName(doc?.meta?.theme);
  const projectSettings = doc?.meta?.settings ?? null;
  const ms = editorRef.current?.transport.ms ?? 0;
  const nowSettings = settingsAt(projectSettings, ms);
  const nowKey = JSON.stringify(nowSettings);
  const settings = useMemo(() => nowSettings, [nowKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const stage = useDuelStage({
    gameUuid, replayId: params.get('replay') ? Number(params.get('replay')) : null,
    theme, settings, stage: stageExtra,
  });
  const {
    match, movieUrl, editor, seekTo, togglePlay, playing, setVideoSize, attachVideo,
    seOn, setSeOn, live, frame, layers, filter,
  } = stage;
  editorRef.current = editor;
  // Space で再生 / 一時停止（文字を打つ欄の中では奪わない）
  useSpaceKey(togglePlay);
  const { loaded, replayId, note } = match;
  const error = listError ?? match.error;

  useEffect(() => {
    api.project('GetDuelListProject').then((rows) => setDuels(rows ?? [])).catch((e) => setListError(String(e?.message ?? e)));
  }, []);
  const duel = duels.find((d) => String(d.id) === duelId) ?? null;
  const games = duel?.games ?? [];

  // ---- プロジェクトファイル（replay の timing）。読めてから保存を始める・変えたときだけ保存
  const storage = useMemo(() => (replayId != null ? createOcgStorage(replayId) : null), [replayId]);
  const savedDoc = useRef(null);
  const [storageReady, setStorageReady] = useState(false);
  const [saveState, setSaveState] = useState(null);
  useEffect(() => {
    setStorageReady(false);
    setSaveState(null);
    if (!storage) { editor.load(emptyDocument(30)); savedDoc.current = editor.doc; return undefined; }
    let alive = true;
    storage.refresh().then((loadedDoc) => {
      if (!alive) return;
      const err = storage.status?.().error;
      if (err) { setSaveState(`調整を読めませんでした（保存しません）: ${err}`); return; }
      editor.load(loadedDoc ?? emptyDocument(30));
      savedDoc.current = editor.doc;
      setStorageReady(true);
      setSaveState('保存済み');
    });
    return () => { alive = false; };
  }, [storage, editor]);
  useEffect(() => {
    if (!storageReady || !storage || editor.doc === savedDoc.current) return undefined;
    setSaveState('未保存');
    const next = editor.doc;
    const timer = setTimeout(async () => {
      setSaveState('保存中…');
      await storage.save(next);
      const err = storage.status?.().error;
      if (err) { setSaveState(`保存に失敗: ${err}`); return; }
      savedDoc.current = next;
      setSaveState('保存済み');
    }, 800);
    return () => clearTimeout(timer);
  }, [editor.doc, storageReady, storage]);

  // ---- 位置合わせの完了（試合のチェックリストの「位置合わせ」。Issue #988）。完了のあとで変えても外さない
  const [timingDone, setTimingDone] = useState(null);
  useEffect(() => {
    setTimingDone(null);
    if (replayId == null) return undefined;
    let alive = true;
    api.duel('OcgGetTiming', { replay_id: replayId })
      .then((r) => { if (alive) setTimingDone(!!r?.timing_done); })
      .catch(() => {});
    return () => { alive = false; };
  }, [replayId]);
  const toggleTimingDone = useCallback(async () => {
    if (replayId == null || timingDone == null) return;
    try {
      const r = await api.duel('OcgSetTimingDone', { replay_id: replayId, done: !timingDone });
      setTimingDone(!!r?.timing_done);
    } catch (err) {
      alert('位置合わせの完了を変えられませんでした: ' + (err?.message ?? err));
    }
  }, [replayId, timingDone]);

  // ---- 文書を変える口（どれも editor のコマンド＝取り消せる）
  const setMeta = useCallback((key, value) => editor.dispatch({ type: 'doc/setMeta', key, value }), [editor]);
  const chooseTheme = useCallback((name) => setMeta('theme', name === DEFAULT_THEME.name ? null : name), [setMeta]);
  const project = useMemo(() => cleanTimingSettings(projectSettings), [projectSettings]);
  const putSettings = useCallback((next) => setMeta('settings', cleanTimingSettings(next)), [setMeta]);
  const [selectedChange, setSelectedChange] = useState(null);
  const selected = project.changes.find((c) => c.id === selectedChange) ?? null;
  const addChange = useCallback(() => {
    const id = nextChangeId(project.changes);
    // 置いた瞬間の値を初めの中身にする（何も変えない変化は保存されないので、フィールドだけ今の値を入れる）
    const at = Math.round(editor.transport.ms);
    putSettings({ ...project, changes: [...project.changes, { id, ms: at, set: { field: settingsAt(project, at).field } }] });
    setSelectedChange(id);
  }, [editor, project, putSettings]);
  const moveChange = useCallback((id, at) => {
    putSettings({ ...project, changes: project.changes.map((c) => (c.id === id ? { ...c, ms: at } : c)) });
  }, [project, putSettings]);
  const editChange = useCallback((id, set) => {
    // 中身が空になったら変化ごと消える（仕様: 変化は 1 項目以上）
    putSettings({ ...project, changes: project.changes.map((c) => (c.id === id ? { ...c, set } : c)) });
  }, [project, putSettings]);
  const removeChange = useCallback((id) => {
    putSettings({ ...project, changes: project.changes.filter((c) => c.id !== id) });
    setSelectedChange(null);
  }, [project, putSettings]);

  const video = (
    <StageVideo
      src={movieUrl} videoRef={attachVideo} onError={setVideoError} preload="metadata"
      onSize={(s) => setVideoSize((prev) => (prev?.w === s.w && prev?.h === s.h ? prev : s))}
    />
  );

  return (
    <div className="page" style={{ maxWidth: 'none' }}>
      {video}
      <div className="page-header">
        <h2>タイミング調節</h2>
        {timingDone != null ? (
          <button type="button" data-testid="timing-done" onClick={toggleTimingDone}
            className={`btn btn-sm ${timingDone ? 'btn-secondary' : 'btn-primary'}`}>
            {timingDone ? '☑ 位置合わせ完了（外す）' : '位置合わせを完了にする'}
          </button>
        ) : null}
        <span style={{ color: '#666', fontSize: 12 }}>
          実写に演出を重ね、演出の時刻を合わせる。演出の見た目の確認は
          {' '}
          <a href={`/duel-editor${duelId && gameUuid ? `?duel=${duelId}&game=${gameUuid}` : ''}`}>演出の確認</a>
        </span>
      </div>

      {error ? <div className="card" style={{ color: '#c00' }}>{error}</div> : null}
      {videoError ? <div className="card" style={{ color: '#c00', whiteSpace: 'pre-wrap' }}>{`元動画が読めません: ${videoError}`}</div> : null}

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 900px', minWidth: 320 }}>
          <div style={{ position: 'relative' }}>
            <Canvas canvas={CANVAS} maxHeight={preview.stageHeight} layers={layers} filter={filter} />
            {preview.fps ? <FpsMeter ms={frame.ms} playing={playing} fps={editor.doc.fps ?? 30} /> : null}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
            <button type="button" data-testid="play" onClick={togglePlay} disabled={!movieUrl} title="Space">再生 / 停止</button>
            <button type="button" onClick={() => seekTo(Math.max(0, editor.transport.ms - 5000))}>-5 秒</button>
            <button type="button" onClick={() => seekTo(editor.transport.ms + 5000)}>+5 秒</button>
            <span style={{ fontSize: 12, color: '#666' }}>
              {`${formatClock(editor.transport.ms)}`}
              {frame.states?.hud?.turn > 0 ? ` / TURN ${frame.states.hud.turn} ${frame.states.hud.phaseLabel ?? ''}` : ''}
              {live.reason ? ` / ${live.reason}` : ''}
              {note ? ` / ${note}` : ''}
            </span>
          </div>
          <div style={{ marginTop: 8 }}>
            <DuelTimeline
              editor={editor} playing={playing} onSeek={seekTo} saveState={saveState} height={260}
              changes={project.changes} selectedChange={selectedChange}
              onSelectChange={setSelectedChange} onMoveChange={moveChange}
            />
          </div>
        </div>

        <div style={panel} data-testid="dt-panel">
          <div style={box}>
            <h4 style={h4}>試合</h4>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <select value={duelId} onChange={(e) => { setDuelId(e.target.value); setGameUuid(''); }}>
                <option value="">デュエル…</option>
                {duels.map((d) => <option key={d.id} value={String(d.id)}>{d.name ?? `#${d.id}`}</option>)}
              </select>
              <select value={gameUuid} onChange={(e) => setGameUuid(e.target.value)} disabled={!games.length}>
                <option value="">試合…</option>
                {games.map((g, i) => <option key={g.game_uuid} value={g.game_uuid}>{g.name || `試合 ${i + 1}`}</option>)}
              </select>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 13 }}>
              テーマ
              <select value={theme.name} onChange={(e) => chooseTheme(e.target.value)} data-testid="dt-theme" disabled={!storageReady}>
                {THEMES.map((t) => <option key={t.name} value={t.name}>{t.label}</option>)}
              </select>
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, fontSize: 13 }}>
              <input type="checkbox" checked={seOn} onChange={(e) => setSeOn(e.target.checked)} />
              SE を鳴らす
            </label>
            <div style={{ marginTop: 6 }}>
              <PreviewSettings qualities={PREVIEW_QUALITIES} value={preview} onChange={changePreview} heightRange={STAGE_HEIGHT} />
            </div>
          </div>

          <div style={box} data-testid="dt-change">
            <h4 style={h4}>設定の変化</h4>
            <button type="button" data-testid="dt-add-change" disabled={!storageReady} onClick={addChange}>
              {`＋ 今の位置（${formatClock(editor.transport.ms)}）に置く`}
            </button>
            <div style={{ fontSize: 12, color: '#666', margin: '6px 0' }}>
              再生位置が印を通ったら、書いた項目だけその値に変わる（次の印まで続く）。印はタイムラインでドラッグして動かせる
            </div>
            {selected ? (
              <div style={{ borderTop: '1px solid #eee', paddingTop: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: 13 }}>
                  <strong>{formatClock(selected.ms)}</strong>
                  <span style={{ color: '#666' }}>{describeChange(selected.set)}</span>
                  <button type="button" style={{ marginLeft: 'auto' }} onClick={() => seekTo(selected.ms)}>ここへ</button>
                  <button type="button" data-testid="dt-remove-change" onClick={() => removeChange(selected.id)}>消す</button>
                </div>
                <SettingsEditor
                  value={selected.set} testId="dt-change-set"
                  defaults={settingsAt(project, Math.max(0, selected.ms - 1))}
                  onChange={(set) => editChange(selected.id, set)}
                />
              </div>
            ) : <div style={{ fontSize: 12, color: '#888' }}>タイムラインの印を押すと中身を変えられます</div>}
          </div>

          <div style={box} data-testid="dt-base">
            <h4 style={h4}>最初の設定</h4>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>試合の頭からの値。「（変えない）」は既定のまま</div>
            <SettingsEditor
              value={project.base} testId="dt-base-set"
              onChange={(base) => putSettings({ ...project, base })}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
